import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AdminCustomers = () => {
  const { user, dashboardUser, getToken, loading: authLoading } = useAuth();
  // Use dashboardUser for dashboard routes
  const currentUser = dashboardUser || user;
  
  console.log('🔍 [AdminCustomers] Component render:', {
    user: user?.role,
    dashboardUser: dashboardUser?.role,
    currentUser: currentUser?.role,
    authLoading
  });
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    is_active: true
  });

  const fetchCustomers = useCallback(async () => {
    try {
      const token = getToken(true); // Dashboard token
      if (!token) {
        console.error('❌ [AdminCustomers] No dashboard token found');
        toast.error('Authentication required. Please login again.');
        navigate('/dashboard/login');
        return;
      }
      
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const url = API_BASE 
        ? `${API_BASE}/api/admin/customers?page=${currentPage}&limit=10&search=${encodeURIComponent(searchTerm)}`
        : `/api/admin/customers?page=${currentPage}&limit=10&search=${encodeURIComponent(searchTerm)}`;
      
      console.log('🔍 [AdminCustomers] Fetching customers from:', url);
      console.log('🔍 [AdminCustomers] Token exists:', !!token);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔍 [AdminCustomers] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch customers' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      console.log('🔍 [AdminCustomers] Raw response text:', responseText.substring(0, 500));
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ [AdminCustomers] Failed to parse JSON:', parseError);
        console.error('❌ [AdminCustomers] Response text:', responseText);
        throw new Error('Invalid JSON response from server');
      }
      
      console.log('🔍 [AdminCustomers] Parsed data:', data);
      console.log('🔍 [AdminCustomers] Data.success:', data.success);
      console.log('🔍 [AdminCustomers] Data.data:', data.data);
      console.log('🔍 [AdminCustomers] Customers array:', data.data?.customers);
      console.log('🔍 [AdminCustomers] Customers count:', data.data?.customers?.length);
      console.log('🔍 [AdminCustomers] Customers type:', Array.isArray(data.data?.customers));
      console.log('🔍 [AdminCustomers] Pagination:', data.data?.pagination);
      
      if (data.success) {
        const customersArray = Array.isArray(data.data?.customers) ? data.data.customers : [];
        console.log('✅ [AdminCustomers] Setting customers array with length:', customersArray.length);
        console.log('✅ [AdminCustomers] Full customers array:', JSON.stringify(customersArray, null, 2));
        if (customersArray.length > 0) {
          console.log('✅ [AdminCustomers] First customer:', {
            id: customersArray[0].id,
            name: `${customersArray[0].first_name} ${customersArray[0].last_name}`,
            email: customersArray[0].email,
            fullObject: customersArray[0]
          });
        } else {
          console.warn('⚠️ [AdminCustomers] customersArray is EMPTY even though data.success is true!');
          console.warn('⚠️ [AdminCustomers] data.data:', data.data);
          console.warn('⚠️ [AdminCustomers] data.data.customers:', data.data?.customers);
        }
        setCustomers(customersArray);
        setTotalPages(data.data?.pagination?.pages || 1);
        console.log('✅ [AdminCustomers] State updated - customers.length should be:', customersArray.length);
      } else {
        console.error('❌ [AdminCustomers] API returned success=false:', data.message);
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('❌ [AdminCustomers] Error fetching customers:', error);
      toast.error(error.message || 'Failed to load customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, getToken, navigate, searchTerm]);

  useEffect(() => {
    console.log('🔍 [AdminCustomers] useEffect triggered:', {
      currentUser: currentUser?.role,
      currentPage,
      searchTerm
    });
    
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'employee')) {
      console.log('✅ [AdminCustomers] User has access, fetching customers...');
      fetchCustomers();
    } else if (!currentUser) {
      console.log('⏳ [AdminCustomers] Waiting for user to load...');
      return;
    } else {
      console.log('❌ [AdminCustomers] User does not have access');
      navigate('/');
    }
  }, [currentPage, currentUser, fetchCustomers, navigate, searchTerm]);

  const fetchCustomerDetails = async (id) => {
    console.log('🔍🔍🔍 [AdminCustomers] fetchCustomerDetails CALLED with ID:', id);
    alert(`Fetching customer details for ID: ${id}`);
    
    try {
      const token = getToken(true); // Dashboard token
      console.log('🔍 [AdminCustomers] Token exists:', !!token);
      
      if (!token) {
        alert('No token found! Please login again.');
        toast.error('Authentication required. Please login again.');
        navigate('/dashboard/login');
        return;
      }
      
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const url = API_BASE ? `${API_BASE}/api/admin/customers/${id}` : `/api/admin/customers/${id}`;
      
      console.log('🔍 [AdminCustomers] Fetching customer details from:', url);
      console.log('🔍 [AdminCustomers] Customer ID:', id);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔍 [AdminCustomers] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch customer details' }));
        alert(`API Error: ${response.status} - ${errorData.message}`);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('🔍 [AdminCustomers] Customer data received:', data);
      console.log('🔍 [AdminCustomers] Customer detail - customer:', data.data?.customer);
      console.log('🔍 [AdminCustomers] Customer detail - orders:', data.data?.orders);
      console.log('🔍 [AdminCustomers] Customer detail - addresses:', data.data?.addresses);
      
      if (data.success && data.data) {
        console.log('✅ [AdminCustomers] Setting selectedCustomer with data:', data.data);
        alert(`Customer data received: ${data.data.customer.first_name} ${data.data.customer.last_name}`);
        setSelectedCustomer(data.data);
        console.log('✅ [AdminCustomers] selectedCustomer should now be set');
      } else {
        alert('API returned success=false');
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('❌ [AdminCustomers] Error fetching customer details:', error);
      alert(`Error: ${error.message}`);
      toast.error(error.message || 'Failed to load customer details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getToken(true); // Dashboard token
      const url = editingCustomer
        ? `/api/admin/customers/${editingCustomer.id}`
        : '/api/admin/customers';
      
      const method = editingCustomer ? 'PUT' : 'POST';
      const body = { ...formData };
      
      if (editingCustomer && !body.password) {
        delete body.password;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(editingCustomer ? 'Customer updated successfully' : 'Customer created successfully');
        setShowForm(false);
        setEditingCustomer(null);
        resetForm();
        fetchCustomers();
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error('Failed to save customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      first_name: customer.first_name,
      last_name: customer.last_name,
      email: customer.email,
      password: '',
      phone: customer.phone || '',
      is_active: customer.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer? This action cannot be undone.')) {
      return;
    }

    try {
      const token = getToken(true); // Dashboard token
      const response = await fetch(`/api/admin/customers/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Customer deleted successfully');
        fetchCustomers();
      } else {
        toast.error(data.message || 'Failed to delete customer');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Failed to delete customer');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: '',
      is_active: true
    });
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCustomer(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Customer Management</h1>
            <p className="mt-2 text-gray-600">Manage customer accounts and information</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            {/* Test button to manually trigger modal */}
            <button
              onClick={() => {
                console.log('🧪 TEST BUTTON CLICKED - Setting dummy customer data');
                setSelectedCustomer({
                  customer: {
                    id: 999,
                    first_name: 'Test',
                    last_name: 'Customer',
                    email: 'test@test.com',
                    phone: '1234567890',
                    is_active: true,
                    created_at: new Date().toISOString()
                  },
                  orders: [],
                  addresses: []
                });
              }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              🧪 Test Modal
            </button>
            {currentUser?.role === 'admin' && (
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Customer
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="xl:hidden divide-y divide-gray-200">
            {customers.length === 0 ? (
              <div className="px-4 py-12 text-center text-gray-500">
                {loading ? 'Loading customers...' : 'No customers found'}
              </div>
            ) : (
              customers.map((customer) => (
                <div key={customer.id} className="p-4 space-y-4">
                  <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="text-base font-semibold text-gray-900">
                        {customer.first_name} {customer.last_name}
                      </div>
                      <div className="text-xs text-gray-500">Customer ID: {customer.id}</div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                        customer.is_active
                          ? 'bg-green-100 text-green-800 border-green-300'
                          : 'bg-red-100 text-red-800 border-red-300'
                      }`}
                    >
                      {customer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Email</div>
                      <div className="mt-1 text-sm text-gray-900 break-all">{customer.email}</div>
                    </div>

                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Phone</div>
                      <div className="mt-1 text-sm text-gray-900">{customer.phone || '-'}</div>
                    </div>

                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Orders</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">{customer.total_orders || 0}</div>
                    </div>

                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Total Spent</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">
                        ${parseFloat(customer.total_spent || 0).toFixed(2)}
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Created</div>
                      <div className="mt-1 text-sm text-gray-900">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <button
                      onClick={() => fetchCustomerDetails(customer.id)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700"
                    >
                      <EyeIcon className="h-4 w-4" />
                      View Details
                    </button>
                    {(currentUser?.role === 'admin' || currentUser?.role === 'employee') && (
                      <button
                        onClick={() => handleEdit(customer)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-4 py-3 text-sm font-medium text-blue-700 hover:bg-blue-100"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit
                      </button>
                    )}
                    {currentUser?.role === 'admin' && (
                      <button
                        onClick={() => handleDelete(customer.id)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700 hover:bg-red-100"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="hidden xl:block overflow-x-auto overscroll-x-contain">
            <table className="min-w-[1100px] divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orders
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Spent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  console.log('🔍 [AdminCustomers] Rendering table - customers.length:', customers.length);
                  console.log('🔍 [AdminCustomers] customers state:', customers);
                  console.log('🔍 [AdminCustomers] loading state:', loading);
                  if (customers.length === 0) {
                    return (
                      <tr>
                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                          {loading ? 'Loading customers...' : 'No customers found'}
                        </td>
                      </tr>
                    );
                  }
                  return customers.map((customer) => {
                    console.log('🔍 [AdminCustomers] Rendering customer:', customer.id, customer.first_name, customer.last_name);
                    return (
                    <tr key={customer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.first_name} {customer.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{customer.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{customer.phone || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{customer.total_orders || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          ${parseFloat(customer.total_spent || 0).toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            customer.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {customer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => fetchCustomerDetails(customer.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          {(currentUser?.role === 'admin' || currentUser?.role === 'employee') && (
                            <button
                              onClick={() => handleEdit(customer)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                          )}
                          {currentUser?.role === 'admin' && (
                            <button
                              onClick={() => handleDelete(customer.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 flex flex-col gap-3 border-t border-gray-200 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingCustomer ? 'Edit Customer' : 'Add Customer'}
                </h2>
                <button
                  onClick={handleCloseForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Password {editingCustomer && '(leave empty to keep current)'}
                  </label>
                  <input
                    type="password"
                    required={!editingCustomer}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                  >
                    {editingCustomer ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {(() => {
        console.log('🔍 [AdminCustomers] Rendering modal check - selectedCustomer:', selectedCustomer);
        console.log('🔍 [AdminCustomers] selectedCustomer exists?', !!selectedCustomer);
        if (selectedCustomer) {
          console.log('🔍 [AdminCustomers] selectedCustomer.customer:', selectedCustomer.customer);
          console.log('🔍 [AdminCustomers] selectedCustomer.orders:', selectedCustomer.orders);
          console.log('🔍 [AdminCustomers] selectedCustomer.addresses:', selectedCustomer.addresses);
        }
        return null;
      })()}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Customer Details</h2>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">First Name</p>
                      <p className="font-semibold text-gray-900">{selectedCustomer.customer?.first_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Last Name</p>
                      <p className="font-semibold text-gray-900">{selectedCustomer.customer?.last_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Email</p>
                      <p className="font-semibold text-gray-900">{selectedCustomer.customer?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Phone</p>
                      <p className="font-semibold text-gray-900">{selectedCustomer.customer?.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Status</p>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        selectedCustomer.customer?.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedCustomer.customer?.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Member Since</p>
                      <p className="font-semibold text-gray-900">
                        {selectedCustomer.customer?.created_at 
                          ? new Date(selectedCustomer.customer.created_at).toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recent Orders */}
                {selectedCustomer.orders && selectedCustomer.orders.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders ({selectedCustomer.orders.length})</h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {selectedCustomer.orders.map((order) => (
                        <div key={order.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                Order #{order.order_number || order.id}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                ${parseFloat(order.total_amount || 0).toFixed(2)}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                order.status === 'DELIVERED' ? 'bg-green-100 text-green-800' :
                                order.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {order.status || 'PENDING'}
                              </span>
                              <p className="text-xs text-gray-500 mt-1">
                                {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                          </div>
                          {order.payment_status && (
                            <p className="text-xs text-gray-500 mt-2">
                              Payment: <span className="capitalize">{order.payment_status}</span>
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Saved Addresses */}
                {selectedCustomer.addresses && selectedCustomer.addresses.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Saved Addresses ({selectedCustomer.addresses.length})</h3>
                    <div className="space-y-3">
                      {selectedCustomer.addresses.map((address, idx) => (
                        <div key={address.id || idx} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <p className="text-sm font-semibold text-gray-900">
                            {address.street || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-600">
                            {address.city}, {address.state} {address.zip_code}
                          </p>
                          <p className="text-sm text-gray-600">{address.country}</p>
                          {address.is_default && (
                            <span className="inline-block mt-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                              Default Address
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;



